import { useState, useEffect, useCallback } from 'react';
import * as orderService from '../services/orderService';
import { playSuccessChime, playSoftPop } from '../utils/audio';

/**
 * useOrders Custom Hook
 * Manages Orders CRUD, forms, multi-event checklists, item checklists, and sound feedback.
 */
export function useOrders() {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Modals state: 'add' | 'edit' | null
  const [activeModal, setActiveModal] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Form Fields
  const [eventDate, setEventDate] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [advancePayment, setAdvancePayment] = useState('');
  const [description, setDescription] = useState('');
  const [requiredThings, setRequiredThings] = useState([]);
  const [eventIds, setEventIds] = useState([]);
  const [person, setPerson] = useState('');
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [isDone, setIsDone] = useState(false);

  // Checklist adding helper state
  const [newThingInput, setNewThingInput] = useState('');

  // Fetch all orders
  const fetchOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await orderService.getAllOrders();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Buyurtmalar ro\'yxatini yuklashda xatolik yuz berdi.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Modal actions
  const openAddModal = useCallback(() => {
    playSoftPop();
    setEventDate('');
    setTotalPrice('');
    setAdvancePayment('');
    setDescription('');
    setRequiredThings([]);
    setEventIds([]);
    setPerson('');
    setPhone1('');
    setPhone2('');
    setIsDone(false);
    setNewThingInput('');
    setFieldErrors({});
    setError('');
    setActiveModal('add');
  }, []);

  // Formats date ISO string to datetime-local format
  const formatIsoToLocalDatetime = (isoVal) => {
    if (!isoVal) return '';
    try {
      // If array format e.g. [year, month, day, hour, minute]
      if (Array.isArray(isoVal)) {
        const [year, month, day, hour = 0, minute = 0] = isoVal;
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      }
      const d = new Date(isoVal);
      if (isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch {
      return '';
    }
  };

  const openEditModal = useCallback((order) => {
    playSoftPop();
    setSelectedOrder(order);
    setEventDate(formatIsoToLocalDatetime(order.eventDate));
    setTotalPrice(order.totalPrice !== undefined ? String(order.totalPrice) : '');
    setAdvancePayment(order.advancePayment !== undefined ? String(order.advancePayment) : '');
    setDescription(order.description || '');
    setRequiredThings(Array.isArray(order.requiredThings) ? [...order.requiredThings] : []);
    
    // Extract event IDs if present in eventDtos
    const ids = Array.isArray(order.eventDtos) ? order.eventDtos.map(e => e.id) : [];
    setEventIds(ids);
    
    setPerson(order.person || '');
    setPhone1(order.phone1 || '');
    setPhone2(order.phone2 || '');
    setIsDone(!!order.isDone);
    setNewThingInput('');
    setFieldErrors({});
    setError('');
    setActiveModal('edit');
  }, []);

  const closeModal = useCallback(() => {
    playSoftPop();
    setActiveModal(null);
    setSelectedOrder(null);
    setEventDate('');
    setTotalPrice('');
    setAdvancePayment('');
    setDescription('');
    setRequiredThings([]);
    setEventIds([]);
    setPerson('');
    setPhone1('');
    setPhone2('');
    setIsDone(false);
    setNewThingInput('');
    setFieldErrors({});
    setError('');
  }, []);

  // Multi-Event Checklist Actions
  const toggleEventId = useCallback((id) => {
    playSoftPop();
    setEventIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }, []);

  // Checklist Actions (requiredThings)
  const addRequiredThing = useCallback(() => {
    if (!newThingInput.trim()) return;
    playSoftPop();
    setRequiredThings((prev) => [...prev, newThingInput.trim()]);
    setNewThingInput('');
  }, [newThingInput]);

  const removeRequiredThing = useCallback((indexToRemove) => {
    playSoftPop();
    setRequiredThings((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  }, []);

  // Client-side Form Validation
  const validateForm = useCallback(() => {
    const errors = {};
    if (!person.trim()) errors.person = 'Mijoz ismi kiritilishi shart.';
    if (!phone1.trim()) errors.phone1 = 'Asosiy telefon raqam kiritilishi shart.';
    
    const parsedTotal = parseFloat(totalPrice);
    if (isNaN(parsedTotal) || parsedTotal <= 0) {
      errors.totalPrice = 'Jami summa musbat son bo\'lishi shart.';
    }

    if (advancePayment.trim()) {
      const parsedAdvance = parseFloat(advancePayment);
      if (isNaN(parsedAdvance) || parsedAdvance < 0) {
        errors.advancePayment = 'Prepayment musbat son bo\'lishi shart.';
      } else if (parsedAdvance > parsedTotal) {
        errors.advancePayment = 'Prepayment jami summadan oshib ketmasligi shart.';
      }
    }

    if (eventIds.length === 0) {
      errors.eventIds = 'Kamida bitta tadbir turi tanlanishi shart.';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [person, phone1, totalPrice, advancePayment, eventIds]);

  // Form Submission
  const handleAddOrder = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    try {
      const dto = {
        eventDate: eventDate ? eventDate : null,
        totalPrice: parseFloat(totalPrice),
        advancePayment: advancePayment ? parseFloat(advancePayment) : 0,
        description: description.trim(),
        requiredThings,
        eventIds,
        person: person.trim(),
        phone1: phone1.trim(),
        phone2: phone2.trim(),
        isDone
      };

      await orderService.addOrder(dto);
      playSuccessChime();
      await fetchOrders();
      setActiveModal(null);
      setSelectedOrder(null);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Buyurtma qo\'shishda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [eventDate, totalPrice, advancePayment, description, requiredThings, eventIds, person, phone1, phone2, isDone, validateForm, fetchOrders]);

  const handleEditOrder = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!selectedOrder) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');
    try {
      const dto = {
        eventDate: eventDate ? eventDate : null,
        totalPrice: parseFloat(totalPrice),
        advancePayment: advancePayment ? parseFloat(advancePayment) : 0,
        description: description.trim(),
        requiredThings,
        eventIds,
        person: person.trim(),
        phone1: phone1.trim(),
        phone2: phone2.trim(),
        isDone
      };

      await orderService.updateOrder(selectedOrder.id, dto);
      playSuccessChime();
      await fetchOrders();
      setActiveModal(null);
      setSelectedOrder(null);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Buyurtmani yangilashda xatolik yuz berdi.');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedOrder, eventDate, totalPrice, advancePayment, description, requiredThings, eventIds, person, phone1, phone2, isDone, validateForm, fetchOrders]);

  // Fast completion status toggle
  const handleToggleDone = useCallback(async (order) => {
    playSoftPop();
    setIsLoading(true);
    try {
      await orderService.toggleOrderStatus(order.id, !order.isDone);
      playSuccessChime();
      await fetchOrders();
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [fetchOrders]);

  return {
    orders,
    isLoading,
    isSubmitting,
    error,
    fieldErrors,
    activeModal,
    selectedOrder,
    eventDate,
    setEventDate,
    totalPrice,
    setTotalPrice,
    advancePayment,
    setAdvancePayment,
    description,
    setDescription,
    requiredThings,
    eventIds,
    toggleEventId,
    person,
    setPerson,
    phone1,
    setPhone1,
    phone2,
    setPhone2,
    isDone,
    setIsDone,
    newThingInput,
    setNewThingInput,
    addRequiredThing,
    removeRequiredThing,
    openAddModal,
    openEditModal,
    closeModal,
    handleAddOrder,
    handleEditOrder,
    handleToggleDone,
    fetchOrders
  };
}
